import React, { useState, useCallback, useRef, useEffect } from "react";
import { MediaUploadComponent } from "./MediaUploadComponent";
import { SearchResultRow } from "./SearchResultRow";
import { usePassioApi } from "../hooks/usePassioApi";
import { OutgoingSearchResult } from "../types";
import { useToolHistory, TOOL_HISTORY_KEYS } from "../hooks/useToolHistory";
import { ToolHistorySection } from "./ToolHistorySection";
import { useBackgroundTasks } from "../contexts/BackgroundTaskContext";
import { useToast } from "../contexts/ToastContext";
import { tokenUsageTracker } from "../services/tokenUsageTracker";

interface GroupedIngredients {
    mealName: string;
    components: {
        componentName: string;
        ingredients: OutgoingSearchResult[];
    }[];
}

interface VisionExtractorData {
    results: OutgoingSearchResult[];
    fileName: string;
    message?: string;
}

export const ExtractIngredientsVision: React.FC = () => {
    const { client } = usePassioApi();
    const { addTask, updateTask } = useBackgroundTasks();
    const { showToast } = useToast();
    const [file, setFile] = useState<File[]>([]);
    const [message, setMessage] = useState("");
    const [isProcessing, setIsProcessing] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [results, setResults] = useState<OutgoingSearchResult[] | null>(null);
    const [isInputCollapsed, setIsInputCollapsed] = useState(false);
    const resultsRef = useRef<HTMLDivElement>(null);

    const {
        history,
        addToHistory,
        clearHistory,
        loadFromHistory,
        selectedHistoryId
    } = useToolHistory<VisionExtractorData>({
        storageKey: TOOL_HISTORY_KEYS.VISION_EXTRACTOR,
        maxEntries: 10
    }, (data) => {
        setResults(data.results);
        setIsInputCollapsed(true);
        setError(null);
    });

    const handleFileReady = useCallback((files: File[]) => {
        setFile(files);
        setError(null);
    }, []);

    const fileToBase64 = (file: File): Promise<string> => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => {
                // Keep the full data URL (e.g., "data:image/jpeg;base64,...")
                const base64 = reader.result as string;
                resolve(base64);
            };
            reader.onerror = error => reject(error);
        });
    };

    const handleSubmit = async () => {
        if (!client || file.length === 0) return;

        setIsProcessing(true);
        setError(null);
        setResults(null);

        // Show start toast
        showToast({
            message: "Analyzing image for ingredients and nutrition...",
            type: "info",
            duration: 3000,
        });

        // Add task to background tasks
        const taskId = addTask({
            type: "vision-extraction" as any,
            status: "running",
            message: `Extracting ingredients from ${file[0].name}`,
        });

        try {
            // Convert file to base64
            const base64Image = await fileToBase64(file[0]);

            // Send as JSON with base64 encoded image
            const response = await client.post<OutgoingSearchResult[]>(
                "/v2/products/sdk/tools/vision/extractIngredientsAutoTyped",
                {
                    message: message.trim() || "",
                    image: base64Image
                },
                undefined,
                { ttl: 0 } // No caching for vision results
            );

            const resultsArray = Array.isArray(response) ? response : [];
            setResults(resultsArray);
            
            // Add to history
            if (resultsArray.length > 0) {
                addToHistory({
                    results: resultsArray,
                    fileName: file[0].name,
                    message: message.trim() || undefined
                });
                setIsInputCollapsed(true);
            }

            // Get token usage data
            const usageData = tokenUsageTracker.getUsageData();

            // Update task status
            updateTask(taskId, {
                status: "completed",
                completedAt: new Date(),
                tokenUsage: usageData?.requestUsage,
            });

            // Show success toast
            showToast({
                message: `Extracted ${resultsArray.length} ingredients with nutrition data!`,
                type: "success",
                duration: 3000,
            });

            // Show token usage if available
            if (usageData && usageData.requestUsage > 0) {
                setTimeout(() => {
                    showToast({
                        message: `Used ${usageData.requestUsage.toLocaleString()} tokens`,
                        type: "info",
                        duration: 5000,
                    });
                }, 500);
            }
        } catch (err) {
            console.error("Error extracting ingredients:", err);
            const errorMessage = err instanceof Error ? err.message : "Failed to extract ingredients";
            setError(errorMessage);

            // Get token usage data for error tracking
            const usageData = tokenUsageTracker.getUsageData();

            // Update task status
            updateTask(taskId, {
                status: "failed",
                completedAt: new Date(),
                error: errorMessage,
                tokenUsage: usageData?.requestUsage,
            });

            // Show error toast
            showToast({
                message: errorMessage,
                type: "error",
                duration: 5000,
            });
        } finally {
            setIsProcessing(false);
        }
    };

    // Auto-scroll to results when they appear
    useEffect(() => {
        if (results && resultsRef.current) {
            setTimeout(() => {
                resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }, 100);
        }
    }, [results]);

    const groupIngredients = (ingredients: OutgoingSearchResult[]): GroupedIngredients[] => {
        const grouped: { [mealName: string]: { [componentName: string]: OutgoingSearchResult[] } } = {};

        ingredients.forEach(ingredient => {
            const mealName = ingredient.mealName || "Unnamed Meal";
            const componentName = ingredient.componentName || "Unnamed Component";

            if (!grouped[mealName]) {
                grouped[mealName] = {};
            }
            if (!grouped[mealName][componentName]) {
                grouped[mealName][componentName] = [];
            }
            grouped[mealName][componentName].push(ingredient);
        });

        // Convert to array format
        return Object.entries(grouped).map(([mealName, components]) => ({
            mealName,
            components: Object.entries(components).map(([componentName, ingredients]) => ({
                componentName,
                ingredients
            }))
        }));
    };

    const handleReset = () => {
        setFile([]);
        setMessage("");
        setResults(null);
        setError(null);
        setIsInputCollapsed(false);
    };

    return (
        <div>
            {/* Collapsible Input Section */}
            <div style={{
                marginBottom: '24px',
                backgroundColor: isInputCollapsed ? '#f9fafb' : 'white',
                borderRadius: '8px',
                border: '1px solid #e5e7eb',
                padding: isInputCollapsed ? '16px' : '24px',
                transition: 'all 0.3s ease',
            }}>
                {isInputCollapsed ? (
                    // Collapsed view
                    <div>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            cursor: 'pointer',
                        }}
                        onClick={() => setIsInputCollapsed(false)}
                        >
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                <span style={{ fontSize: '20px' }}>📸</span>
                                <div>
                                    <div style={{
                                        fontSize: '16px',
                                        fontWeight: '600',
                                        color: '#111827',
                                    }}>
                                        {file[0]?.name || 'No file selected'}
                                    </div>
                                    {message && (
                                        <div style={{
                                            fontSize: '14px',
                                            color: '#6b7280',
                                            marginTop: '2px',
                                        }}>
                                            Context: {message}
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleReset();
                                    }}
                                    style={{
                                        padding: '6px 12px',
                                        backgroundColor: '#fee2e2',
                                        color: '#dc2626',
                                        border: 'none',
                                        borderRadius: '6px',
                                        fontSize: '14px',
                                        fontWeight: '500',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s',
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.backgroundColor = '#fecaca';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.backgroundColor = '#fee2e2';
                                    }}
                                >
                                    New Scan
                                </button>
                                <span style={{
                                    fontSize: '14px',
                                    color: '#6b7280',
                                }}>
                                    ▼
                                </span>
                            </div>
                        </div>
                    </div>
                ) : (
                    // Expanded view
                    <div>
                        <h3 style={{
                            fontSize: '18px',
                            fontWeight: '600',
                            color: '#111827',
                            marginBottom: '16px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                        }}>
                            <span>📸</span>
                            Upload Image or Video
                        </h3>
                        <p style={{
                            fontSize: '14px',
                            color: '#6b7280',
                            marginBottom: '16px',
                            lineHeight: '1.5',
                        }}>
                            Upload a photo or video of a meal, recipe, or food items. Our AI will extract detailed ingredient information with portions and nutrition data.
                        </p>
                        
                        <MediaUploadComponent
                            maxFiles={1}
                            onFilesReady={handleFileReady}
                            disabled={isProcessing}
                        />

                        {/* Optional Context Input */}
                        <div style={{ marginTop: '16px' }}>
                            <label style={{
                                display: 'block',
                                fontSize: '14px',
                                fontWeight: '500',
                                color: '#374151',
                                marginBottom: '8px',
                            }}>
                                Additional Context (Optional)
                            </label>
                            <input
                                type="text"
                                value={message}
                                onChange={(e) => setMessage(e.target.value)}
                                placeholder="e.g., 'This is for 4 servings' or 'Focus on the main dish'"
                                style={{
                                    width: '100%',
                                    padding: '10px 12px',
                                    fontSize: '14px',
                                    borderRadius: '6px',
                                    border: '1px solid #e5e7eb',
                                    transition: 'border-color 0.2s',
                                }}
                                onFocus={(e) => {
                                    e.currentTarget.style.borderColor = '#3b82f6';
                                    e.currentTarget.style.outline = 'none';
                                    e.currentTarget.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
                                }}
                                onBlur={(e) => {
                                    e.currentTarget.style.borderColor = '#e5e7eb';
                                    e.currentTarget.style.boxShadow = 'none';
                                }}
                                disabled={isProcessing}
                            />
                        </div>

                        <button
                            onClick={handleSubmit}
                            disabled={file.length === 0 || isProcessing || !client}
                            style={{
                                marginTop: '16px',
                                width: '100%',
                                padding: '12px',
                                backgroundColor: file.length === 0 || isProcessing ? '#e5e7eb' : '#3b82f6',
                                color: file.length === 0 || isProcessing ? '#9ca3af' : 'white',
                                border: 'none',
                                borderRadius: '8px',
                                fontSize: '16px',
                                fontWeight: '600',
                                cursor: file.length === 0 || isProcessing ? 'not-allowed' : 'pointer',
                                transition: 'all 0.2s',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                            onMouseEnter={(e) => {
                                if (file.length > 0 && !isProcessing) {
                                    e.currentTarget.style.backgroundColor = '#2563eb';
                                }
                            }}
                            onMouseLeave={(e) => {
                                if (file.length > 0 && !isProcessing) {
                                    e.currentTarget.style.backgroundColor = '#3b82f6';
                                }
                            }}
                        >
                            {isProcessing ? (
                                <>
                                    <span style={{ marginRight: '8px' }}>⏳</span>
                                    Processing...
                                </>
                            ) : (
                                <>
                                    <span style={{ marginRight: '8px' }}>🔍</span>
                                    Extract Ingredients
                                </>
                            )}
                        </button>
                    </div>
                )}
            </div>

            {/* History Section */}
            <ToolHistorySection
                history={history}
                selectedHistoryId={selectedHistoryId}
                onLoadHistory={loadFromHistory}
                onClearHistory={clearHistory}
                formatDescription={(entry) => 
                    `${entry.data.results.length} ingredients from ${entry.data.fileName}`
                }
                title="Recent Extractions"
            />

            {/* Error Display */}
            {error && (
                <div style={{
                    padding: '16px',
                    backgroundColor: '#fee2e2',
                    color: '#dc2626',
                    borderRadius: '8px',
                    marginBottom: '24px',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '12px',
                    border: '1px solid #fecaca',
                }}>
                    <span style={{ fontSize: '20px', marginTop: '-2px' }}>⚠️</span>
                    <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: '600', marginBottom: '4px' }}>Error</div>
                        <div style={{ fontSize: '14px' }}>{error}</div>
                    </div>
                </div>
            )}

            {/* Results Display */}
            <div ref={resultsRef}>
                {results && results.length > 0 && (
                    <div style={{
                        backgroundColor: '#f0fdf4',
                        borderRadius: '12px',
                        border: '2px solid #10b981',
                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                        animation: 'slideIn 0.3s ease-out',
                        overflow: 'hidden',
                    }}>
                        <style>
                            {`
                                @keyframes slideIn {
                                    from {
                                        opacity: 0;
                                        transform: translateY(20px);
                                    }
                                    to {
                                        opacity: 1;
                                        transform: translateY(0);
                                    }
                                }
                            `}
                        </style>
                        <div style={{
                            padding: '24px',
                            borderBottom: '1px solid #d1fae5',
                        }}>
                            <h3 style={{
                                fontSize: '20px',
                                fontWeight: '700',
                                color: '#065f46',
                                margin: 0,
                                display: 'flex',
                                alignItems: 'center',
                                gap: '12px',
                            }}>
                                <span style={{ fontSize: '28px' }}>✅</span>
                                Successfully Extracted {results.length} Ingredient{results.length !== 1 ? 's' : ''}
                            </h3>
                        </div>

                        {/* Group and display ingredients - FULL WIDTH */}
                        <div style={{
                            padding: '24px',
                            backgroundColor: 'white',
                        }}>
                            {groupIngredients(results).map((meal, mealIndex) => (
                                <div key={mealIndex} style={{
                                    marginBottom: mealIndex < groupIngredients(results).length - 1 ? '24px' : 0,
                                }}>
                                    <h4 style={{
                                        fontSize: '18px',
                                        fontWeight: '600',
                                        color: '#1f2937',
                                        marginBottom: '16px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        paddingBottom: '12px',
                                        borderBottom: '1px solid #e5e7eb',
                                    }}>
                                        <span style={{ marginRight: '10px', fontSize: '24px' }}>🍽️</span>
                                        {meal.mealName}
                                    </h4>

                                    {meal.components.map((component, componentIndex) => (
                                        <div key={componentIndex} style={{
                                            marginBottom: '16px',
                                        }}>
                                            {component.componentName !== "Unnamed Component" && (
                                                <h5 style={{
                                                    fontSize: '16px',
                                                    fontWeight: '600',
                                                    color: '#4b5563',
                                                    marginBottom: '12px',
                                                    marginLeft: '34px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                }}>
                                                    <span style={{ marginRight: '8px', fontSize: '18px' }}>📦</span>
                                                    {component.componentName}
                                                </h5>
                                            )}

                                            <div style={{
                                                display: 'grid',
                                                gap: '8px',
                                                marginLeft: component.componentName !== "Unnamed Component" ? '40px' : '20px',
                                            }}>
                                                {component.ingredients.map((ingredient, index) => (
                                                    <SearchResultRow
                                                        key={index}
                                                        searchResult={ingredient}
                                                        showDetailedNutrients={false}
                                                    />
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* No results message */}
                {results && results.length === 0 && (
                    <div style={{
                        padding: '40px',
                        backgroundColor: '#f9fafb',
                        borderRadius: '12px',
                        textAlign: 'center',
                        color: '#6b7280',
                        border: '1px dashed #e5e7eb',
                    }}>
                        <span style={{ fontSize: '48px', display: 'block', marginBottom: '16px' }}>🔍</span>
                        <div style={{ fontSize: '16px', fontWeight: '500' }}>
                            No ingredients were detected in the uploaded media.
                        </div>
                        <div style={{ fontSize: '14px', marginTop: '8px' }}>
                            Try uploading a clearer image or video of food items.
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}; 